import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { debounce } from 'lodash';
import { ContentfulService } from '~contentful/services/contentful.service';
import { ETagProduct, ETagType, GiftCard, Vendor } from '~models';
import {
  GiftCardsService,
  LoaderService,
  OptimizelyService,
  TrxService,
  ZipYiService,
} from '~services';
import { PageModel } from '~contentful/models/components/page.model';
import { environment } from '../../../environments/environment';
import { AMAZON_CARD_ID } from '../checkout/checkout.component';
import {
  EStaticCategory,
  GiftcardCategoryService,
} from 'src/app/shared-data/giftcard-category.service';
import { ZipYiContext } from '~services/biocatch/zip-yi-context.enum';
import { ZipYiElement } from '~services/biocatch/zip-yi-element.enum';
import { FeatureFlags } from '~services/optimizely/feature-flags.enum';

@Component({
  selector: 'app-home-view',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeViewComponent implements OnInit {
  public giftCards: GiftCard[] = [];
  public featuredGiftCards: GiftCard[] = [];
  public searchedGiftCards: GiftCard[];
  public zipMoneyCashbackGiftCards: GiftCard[] = [];
  public otherPromoGiftCards: GiftCard[] = [];
  public appPrefix = environment.appPrefix;
  public env = environment.environment;
  public enableVendor = this.ngActivatedRoute.snapshot.queryParams.enable;
  public featured = [];
  public categories = [];
  public categoryFilterGiftCards: GiftCard[] = [];
  public staticCategory = EStaticCategory;
  public zipYiElement = ZipYiElement;
  public showMaintenanceMessage = false;
  public ui_code = this.ngActivatedRoute.snapshot.queryParams.ui_code; // temporarily used to hide/show vendors

  public readonly page$: Observable<PageModel>;

  constructor(
    private readonly contentfulService: ContentfulService,
    private giftCardService: GiftCardsService,
    private ngRouter: Router,
    private ngActivatedRoute: ActivatedRoute,
    public giftcardCategoryService: GiftcardCategoryService,
    private zipYiService: ZipYiService,
    private loaderService: LoaderService,
    private optimizelyService: OptimizelyService,
    private trxService: TrxService,
  ) {
    this.page$ = contentfulService.getPage('giftcards').pipe(shareReplay(1));
  }

  async ngOnInit() {
    try {
      this.loaderService.show("We're fetching your Gift Cards.");
      await this.getCards(this.enableVendor, this.ui_code);
      this.loaderService.hide();
    } catch (error) {
      window.zbm?.reportError(error);
      this.ngRouter.navigate(['/error/unavailable']);
    }
    this.zipYiService.setContext(ZipYiContext.HomePage);
    this.showMaintenanceMessage = await this.optimizelyService.isFeatureEnabled(
      FeatureFlags.ShowMaintenanceMessage,
    );
  }

  async getCards(enableVendor, ui_code) {
    [this.giftCards, this.featured, this.categories] = await Promise.all([
      this.giftCardService.getCards(enableVendor, ui_code),
      this.giftCardService.getFeaturedCards(),
      this.giftCardService.getCardCategories(),
    ]);

    // filter and sort zip money specific cashback card
    this.zipMoneyCashbackGiftCards = this.giftCards
      .filter((c) =>
        c.tags?.find(
          (t) =>
            t.type === ETagType.CASHBACK &&
            t.products?.length === 1 &&
            t.products[0] === ETagProduct.ZM,
        ),
      )
      .sort((a, b) => a.brand.localeCompare(b.brand));

    // filter and sort non-zip-money promo card
    this.otherPromoGiftCards = this.giftCards
      .filter((c) =>
        c.tags?.find(
          (t) =>
            t.products?.length !== 1 ||
            (t.products?.length === 1 && t.products[0] !== ETagProduct.ZM),
        ),
      )
      .sort((a, b) => a.brand.localeCompare(b.brand));

    this.featuredGiftCards = this.giftCards
      .filter((card) => this.featured.find((featured) => featured === card.id) !== undefined)
      .map((card) => ({
        ...card,
        brand:
          card.vendor === Vendor.DigitalGlue ? card.brand.replace(/Store\s?card/i, '') : card.brand,
      }));
  }

  trackSearch = debounce(() => {
    if (this.giftcardCategoryService.searchGiftCardsInput) {
      this.trxService.sendUIInputEvent({
        control_id: ZipYiElement.GiftCardSearchInput,
        label: 'Search for gift cards',
        input_type: 'input',
        new_value: this.giftcardCategoryService.searchGiftCardsInput,
        previous_value: null,
      });
    }
  }, 500);

  search() {
    this.giftcardCategoryService.setSelectedCategoryId(EStaticCategory.All);
    this.searchedGiftCards = this.giftCardService.search(
      this.giftcardCategoryService.searchGiftCardsInput,
    );

    this.trackSearch();
  }

  viewAllZipMoneyDeals() {
    this.giftcardCategoryService.setSelectedCategoryId(EStaticCategory.ZipMoneyDeals);
    this.giftcardCategoryService.setSearchGiftCardsInput('');
  }

  public getCardBrand(card: GiftCard) {
    return card.vendor === Vendor.DigitalGlue
      ? card.id === AMAZON_CARD_ID
        ? 'Amazon.com.au eGift Card'
        : card.brand.replace(/Store\s?card/i, '').trim()
      : card.brand;
  }
}
